import React from 'react';
import { MapPin } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  actionPriorityBadgeVariant,
  formatActionDate,
  isActionOverdue,
  resolveActionAssignee,
  type AssigneeDirectory,
} from './actionEditModel';
import { getActionCoordinates } from '../utils/actionGeolocation';
import { getActionMapId } from '../utils/actionMapId';
import { SubTaskCompletionRing } from '../SubTaskCompletionRing';
import type { ActionEntity } from '../hooks/useScopedActionForm';

type Props = {
  action: ActionEntity;
  directory: AssigneeDirectory;
  onZoomToMap?: (action: ActionEntity) => void;
};

/**
 * One task on the board. Status lives on the column, so the card spends its
 * space on who owns it, when it is due, and whether it is already a problem.
 * Clicking the card opens the task modal; the map pin only zooms the map.
 */
export const ActionKanbanCard: React.FC<Props> = ({
  action,
  directory,
  onZoomToMap,
}) => {
  const overdue = isActionOverdue(action.status, action.target_date);
  const assignee = resolveActionAssignee(action, directory);
  const coords = getActionCoordinates(
    (action as { geolocation?: unknown }).geolocation
  );
  const priority = action.priority ?? 'Normal';

  return (
    <Card
      size="sm"
      id={`action-row-${getActionMapId(action)}`}
      data-tour="hwcg-task-card"
      className={`tw:cursor-pointer tw:gap-2 tw:py-2 ${
        overdue ? 'tw:ring-destructive/50' : ''
      }`}
    >
      <CardContent className="tw:px-3 tw:space-y-1.5">
        <div className="tw:flex tw:items-start tw:justify-between tw:gap-2">
          <div className="tw:min-w-0 tw:font-medium tw:leading-snug">
            {action.name}
          </div>
          <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-1">
            {overdue ? (
              <Badge variant="destructive" className="tw:text-[0.65rem]">
                Overdue
              </Badge>
            ) : null}
            {onZoomToMap ? (
              <Button
                type="button"
                size="xs"
                variant="ghost"
                disabled={!coords}
                aria-label={
                  coords
                    ? `Zoom map to ${action.name}`
                    : `${action.name} has no location`
                }
                onClick={(event) => {
                  event.stopPropagation();
                  if (coords) onZoomToMap(action);
                }}
              >
                <MapPin className="tw:size-3.5" />
              </Button>
            ) : null}
          </div>
        </div>
        <SubTaskCompletionRing checklist={action.checklist} />
        {action.description ? (
          <p className="tw:m-0 tw:line-clamp-2 tw:text-xs tw:text-foreground">
            {action.description}
          </p>
        ) : null}
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
          <Badge variant={actionPriorityBadgeVariant(priority)}>
            {priority}
          </Badge>
          <span className="tw:text-xs tw:text-foreground">
            {assignee.label}
          </span>
        </div>
        <div className="tw:text-xs tw:text-foreground">
          Due {formatActionDate(action.target_date)}
        </div>
      </CardContent>
    </Card>
  );
};

export default ActionKanbanCard;
